import { create } from 'zustand';
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  serverTimestamp,
  writeBatch
} from 'firebase/firestore';
import { deleteObject, getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { db, storage } from '../services/firebase';
import { COLLECTIONS } from '../services/firestore';

const DOCUMENT_PAGES_SUBCOLLECTION = 'pages';
const pageDocId = (index) => String(index);
const MAX_PDF_SIZE_BYTES = 25 * 1024 * 1024;
const MAX_AUDIO_SIZE_BYTES = 50 * 1024 * 1024;
const persistedDocumentPages = new Map();
const documentWriteChains = new Map();

const enqueueDocumentWrite = (documentId, write) => {
  const previous = documentWriteChains.get(documentId) || Promise.resolve();
  const queued = previous.catch(() => {}).then(write);
  const tracked = queued.finally(() => {
    if (documentWriteChains.get(documentId) === tracked) {
      documentWriteChains.delete(documentId);
    }
  });
  documentWriteChains.set(documentId, tracked);
  return queued;
};

const sanitizeFilename = (name = 'document.pdf') =>
  name.replace(/[^a-zA-Z0-9._-]/g, '_');

const resolveAudioExtension = (contentType = '', fallbackName = 'document-narration.webm') => {
  if (contentType.includes('mpeg')) return 'mp3';
  if (contentType.includes('wav')) return 'wav';
  if (contentType.includes('ogg')) return 'ogg';
  if (contentType.includes('mp4') || contentType.includes('m4a')) return 'm4a';
  if (contentType.includes('webm')) return 'webm';
  const parts = String(fallbackName).split('.');
  return parts.length > 1 ? parts.pop() : 'webm';
};

const saveDocumentPages = async (documentId, pages) => {
  const batch = writeBatch(db);
  const pagesRef = collection(db, COLLECTIONS.DOCUMENTS, documentId, DOCUMENT_PAGES_SUBCOLLECTION);
  let previousPages = persistedDocumentPages.get(documentId);
  let writeCount = 0;

  if (!previousPages) {
    const existing = await getDocs(pagesRef);
    previousPages = [];
    existing.forEach((snap) => {
      const data = snap.data();
      const index = Number.isInteger(data.index) ? data.index : Number(snap.id);
      if (Number.isInteger(index) && index >= 0) previousPages[index] = data.html || '';
    });
  }

  if (previousPages.length > pages.length) {
    for (let i = pages.length; i < previousPages.length; i += 1) {
      batch.delete(doc(pagesRef, pageDocId(i)));
      writeCount += 1;
    }
  }

  pages.forEach((html, index) => {
    if (previousPages[index] === html) return;
    const pageRef = doc(pagesRef, pageDocId(index));
    batch.set(pageRef, {
      index,
      html,
      updatedAt: serverTimestamp()
    });
    writeCount += 1;
  });

  if (writeCount > 0) await batch.commit();
  persistedDocumentPages.set(documentId, [...pages]);
};

const loadDocumentPages = async (documentId) => {
  const pagesRef = collection(db, COLLECTIONS.DOCUMENTS, documentId, DOCUMENT_PAGES_SUBCOLLECTION);
  const q = query(pagesRef, orderBy('index', 'asc'));
  const snapshot = await getDocs(q);
  return snapshot.docs.map((pageDoc) => pageDoc.data().html || '');
};

const isDocumentSharedWithUser = async (documentId, userId) => {
  if (!documentId || !userId) return false;
  try {
    const sharedDocRef = doc(db, COLLECTIONS.DOCUMENTS, documentId, 'sharedUsers', userId);
    const sharedDocSnap = await getDoc(sharedDocRef);
    return sharedDocSnap.exists();
  } catch (error) {
    console.error('Failed to verify document share access:', error);
    return false;
  }
};

const canUserAccessDocument = async ({ documentId, ownerId, currentUserId, isAdmin, publicShareEnabled = false }) => {
  if (isAdmin) return true;
  if (ownerId && currentUserId && ownerId === currentUserId) return true;
  if (publicShareEnabled) return true;
  if (!currentUserId) return false;
  return isDocumentSharedWithUser(documentId, currentUserId);
};

const collectNarrationPaths = (narration) => {
  const paths = new Set();
  if (!narration) return [];
  if (narration.path) paths.add(narration.path);
  Object.values(narration.pages || {}).forEach((item) => {
    if (item?.path) paths.add(item.path);
  });
  return Array.from(paths);
};

export const useDocumentStore = create((set, get) => ({
  // State
  documents: [],
  currentDocument: null,
  isLoading: false,
  error: null,

  uploadAudioResource: async (file, ownerId, existingNarrationPath = null, metadata = {}) => {
    if (!file) {
      throw new Error('No audio file selected');
    }
    if (!ownerId) {
      throw new Error('Missing document owner');
    }
    if (!(file.type || '').startsWith('audio/')) {
      throw new Error('Only audio files are supported');
    }
    if (file.size > MAX_AUDIO_SIZE_BYTES) {
      throw new Error('Audio must be under 50 MB');
    }

    const extension = resolveAudioExtension(file.type, file.name || 'document-narration.webm');
    const safeName = sanitizeFilename(file.name || `document-narration.${extension}`);
    const path = `resources/${ownerId}/sigma-document-narration-${Date.now()}-${safeName}`;
    const fileRef = ref(storage, path);
    await uploadBytes(fileRef, file, { contentType: file.type || 'audio/webm' });
    const url = await getDownloadURL(fileRef);

    if (existingNarrationPath) {
      await deleteObject(ref(storage, existingNarrationPath)).catch(() => {});
    }

    const narration = {
      name: file.name || safeName,
      path,
      url,
      size: file.size,
      contentType: file.type || 'audio/webm',
      duration: metadata.duration || 0,
      uploadedAt: new Date().toISOString()
    };

    return narration;
  },

  removeAudioResource: async (narrationPath = null) => {
    const resolvedPath = narrationPath || null;
    if (resolvedPath) {
      await deleteObject(ref(storage, resolvedPath)).catch(() => {});
    }
  },

  uploadDocumentNarration: async (file, ownerId, existingNarrationPath = null, metadata = {}) => {
    const narration = await get().uploadAudioResource(file, ownerId, existingNarrationPath, metadata);
    const currentDocument = get().currentDocument;
    if (currentDocument) {
      set({
        currentDocument: {
          ...currentDocument,
          narration
        }
      });
    }
    return narration;
  },

  removeDocumentNarration: async (narrationPath = null) => {
    const currentDocument = get().currentDocument;
    const resolvedPath = narrationPath || currentDocument?.narration?.path || null;

    await get().removeAudioResource(resolvedPath);

    if (currentDocument) {
      set({
        currentDocument: {
          ...currentDocument,
          narration: null
        }
      });
    }
  },

  uploadPdfFile: async (file, ownerId, existingPdfPath = null) => {
    if (!file) {
      throw new Error('No PDF file selected');
    }
    if (file.type !== 'application/pdf') {
      throw new Error('Only PDF files are supported');
    }
    if (!ownerId) {
      throw new Error('Missing document owner');
    }
    if (file.size > MAX_PDF_SIZE_BYTES) {
      throw new Error('PDF must be under 25 MB');
    }

    const safeName = sanitizeFilename(file.name || 'document.pdf');
    const path = `resources/${ownerId}/sigma-pdf-${Date.now()}-${safeName}`;
    const fileRef = ref(storage, path);
    await uploadBytes(fileRef, file, { contentType: 'application/pdf' });
    const url = await getDownloadURL(fileRef);

    const pdf = {
      name: file.name || safeName,
      path,
      url,
      size: file.size,
      contentType: 'application/pdf',
      uploadedAt: new Date().toISOString()
    };

    const currentDocument = get().currentDocument;
    if (currentDocument) {
      set({
        currentDocument: {
          ...currentDocument,
          pdf,
          contentType: 'pdf'
        }
      });
    }

    if (existingPdfPath && existingPdfPath !== path) {
      deleteObject(ref(storage, existingPdfPath)).catch(() => {});
    }

    return pdf;
  },

  removePdfFile: async (pdfPath = null) => {
    const currentDocument = get().currentDocument;
    const resolvedPath = pdfPath || currentDocument?.pdf?.path || null;

    if (resolvedPath) {
      await deleteObject(ref(storage, resolvedPath)).catch(() => {});
    }

    if (currentDocument) {
      set({
        currentDocument: {
          ...currentDocument,
          pdf: null,
          contentType: currentDocument.contentType === 'pdf' ? 'editor' : currentDocument.contentType
        }
      });
    }
  },

  // Create document
  createDocument: async (documentData) => {
    try {
      set({ isLoading: true, error: null });

      const pages = Array.isArray(documentData.pages) && documentData.pages.length > 0
        ? documentData.pages
        : [''];

      const newDocument = {
        ...documentData,
        pages,
        pageCount: pages.length,
        contentType: documentData.contentType === 'pdf' ? 'pdf' : 'editor',
        pdf: documentData.pdf || null,
        narration: documentData.narration || null,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        status: 'draft'
      };

      const { pages: _pages, ...documentPayload } = newDocument;
      const docRef = await addDoc(collection(db, COLLECTIONS.DOCUMENTS), documentPayload);
      persistedDocumentPages.set(docRef.id, []);
      await saveDocumentPages(docRef.id, pages);

      const createdDocument = {
        id: docRef.id,
        ...newDocument,
        createdAt: new Date(),
        updatedAt: new Date()
      };

      set(state => ({
        documents: [...state.documents, createdDocument],
        currentDocument: createdDocument,
        isLoading: false
      }));

      return createdDocument;
    } catch (error) {
      console.error('Error creating document:', error);
      set({ error: error.message, isLoading: false });
      throw error;
    }
  },

  // Set current document
  setCurrentDocument: (document) => {
    set({ currentDocument: document });
  },

  // Update document
  updateDocument: async (documentId, updates) => {
    return enqueueDocumentWrite(documentId, async () => {
      try {
        set({ isLoading: true, error: null });

      const nextPages = Array.isArray(updates.pages) ? updates.pages : null;
      const stateUpdates = nextPages ? { ...updates, pageCount: nextPages.length } : updates;

      const updateData = {
        ...updates,
        updatedAt: serverTimestamp()
      };

      if (nextPages) {
        updateData.pageCount = nextPages.length;
      } else if (typeof updates.pageCount === 'number') {
        updateData.pageCount = updates.pageCount;
      }

      if (nextPages) {
        delete updateData.pages;
        await saveDocumentPages(documentId, nextPages);
      }

      const sanitizedUpdateData = Object.fromEntries(
        Object.entries(updateData).filter(([, value]) => value !== undefined)
      );

      await updateDoc(doc(db, COLLECTIONS.DOCUMENTS, documentId), sanitizedUpdateData);

        set(state => {
          const currentPages = state.currentDocument?.pages;
          const currentDocumentUpdates = nextPages
            ? {
                ...stateUpdates,
                pages: currentPages,
                pageCount: Array.isArray(currentPages) ? currentPages.length : nextPages.length
              }
            : stateUpdates;
          return {
            documents: state.documents.map(document =>
              document.id === documentId
                ? { ...document, ...stateUpdates, updatedAt: new Date() }
                : document
            ),
            currentDocument: state.currentDocument?.id === documentId
              ? { ...state.currentDocument, ...currentDocumentUpdates, updatedAt: new Date() }
              : state.currentDocument,
            isLoading: false
          };
        });
      } catch (error) {
        console.error('Error updating document:', error);
        set({ error: error.message, isLoading: false });
        throw error;
      }
    });
  },

  // Save document (alias for update with current state)
  saveDocument: async (documentId, data) => {
    return get().updateDocument(documentId, data);
  },

  // Load all documents for a user
  loadDocuments: async (userId) => {
    try {
      set({ isLoading: true, error: null });

      const q = query(
        collection(db, COLLECTIONS.DOCUMENTS),
        where('userId', '==', userId),
        orderBy('updatedAt', 'desc')
      );

      const querySnapshot = await getDocs(q);
      const documents = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date(),
        updatedAt: doc.data().updatedAt?.toDate() || new Date()
      }));

      set({ documents, isLoading: false });
      return documents;
    } catch (error) {
      console.error('Error loading documents:', error);
      set({ error: error.message, isLoading: false });
      throw error;
    }
  },

  // Load single document
  loadDocument: async (documentId, options = {}) => {
    try {
      set({ isLoading: true, error: null });
      const { currentUserId = null, isAdmin = false } = options;

      const docSnap = await getDoc(doc(db, COLLECTIONS.DOCUMENTS, documentId));

      if (docSnap.exists()) {
        let pages = [];
        const ownerId = docSnap.data().userId || docSnap.data().ownerId || null;
        const hasAccess = await canUserAccessDocument({
          documentId: docSnap.id,
          ownerId,
          currentUserId,
          isAdmin,
          publicShareEnabled: docSnap.data().publicShareEnabled === true
        });
        if (!hasAccess) {
          throw new Error('Access denied');
        }

        try {
          pages = await loadDocumentPages(docSnap.id);
        } catch (err) {
          console.warn('Failed to load document pages from subcollection:', err);
        }

        const document = {
          id: docSnap.id,
          ...docSnap.data(),
          pages: pages.length ? pages : (docSnap.data().pages || ['']),
          pageCount: pages.length ? pages.length : (docSnap.data().pageCount || (docSnap.data().pages?.length ?? 1)),
          contentType: docSnap.data().contentType === 'pdf' ? 'pdf' : 'editor',
          pdf: docSnap.data().pdf || null,
          narration: docSnap.data().narration || null,
          createdAt: docSnap.data().createdAt?.toDate() || new Date(),
          updatedAt: docSnap.data().updatedAt?.toDate() || new Date()
        };

        persistedDocumentPages.set(docSnap.id, [...document.pages]);
        set({ currentDocument: document, isLoading: false });
        return document;
      } else {
        throw new Error('Document not found');
      }
    } catch (error) {
      console.error('Error loading document:', error);
      set({ error: error.message, isLoading: false });
      throw error;
    }
  },

  // Delete document
  deleteDocument: async (documentId) => {
    try {
      set({ isLoading: true, error: null });

      const knownDocument =
        get().currentDocument?.id === documentId
          ? get().currentDocument
          : get().documents.find((item) => item.id === documentId) || null;

      if (knownDocument?.pdf?.path) {
        await deleteObject(ref(storage, knownDocument.pdf.path)).catch(() => {});
      }
      const narrationPaths = collectNarrationPaths(knownDocument?.narration);
      for (const narrationPath of narrationPaths) {
        await deleteObject(ref(storage, narrationPath)).catch(() => {});
      }

      await deleteDoc(doc(db, COLLECTIONS.DOCUMENTS, documentId));
      persistedDocumentPages.delete(documentId);

      set(state => ({
        documents: state.documents.filter(d => d.id !== documentId),
        currentDocument: state.currentDocument?.id === documentId
          ? null
          : state.currentDocument,
        isLoading: false
      }));
    } catch (error) {
      console.error('Error deleting document:', error);
      set({ error: error.message, isLoading: false });
      throw error;
    }
  },

  // Clear current document
  clearCurrentDocument: () => {
    set({ currentDocument: null });
  },

  // ─── Assessment-on-document actions ────────────────────────
  enableAssessment: () => {
    const doc = get().currentDocument;
    if (!doc) return;
    set({
      currentDocument: {
        ...doc,
        assessment: {
          enabled: true,
          category: 'formal',
          informalType: null,
          showModelAnswers: false,
          timeLimit: null,
          validFrom: null,
          validUntil: null,
          responseAreas: {},
          responseAreaOrder: [],
        },
      },
    });
  },

  disableAssessment: () => {
    const doc = get().currentDocument;
    if (!doc) return;
    set({ currentDocument: { ...doc, assessment: null } });
  },

  updateAssessmentConfig: (config) => {
    const doc = get().currentDocument;
    if (!doc?.assessment) return;
    set({
      currentDocument: {
        ...doc,
        assessment: { ...doc.assessment, ...config },
      },
    });
  },

  addResponseArea: (blockId, config) => {
    const doc = get().currentDocument;
    if (!doc?.assessment) return;
    const responseAreas = { ...doc.assessment.responseAreas, [blockId]: config };
    const responseAreaOrder = [...doc.assessment.responseAreaOrder, blockId];
    set({
      currentDocument: {
        ...doc,
        assessment: { ...doc.assessment, responseAreas, responseAreaOrder },
      },
    });
  },

  updateResponseArea: (blockId, updates) => {
    const doc = get().currentDocument;
    if (!doc?.assessment?.responseAreas?.[blockId]) return;
    const responseAreas = {
      ...doc.assessment.responseAreas,
      [blockId]: { ...doc.assessment.responseAreas[blockId], ...updates },
    };
    set({
      currentDocument: {
        ...doc,
        assessment: { ...doc.assessment, responseAreas },
      },
    });
  },

  removeResponseArea: (blockId) => {
    const doc = get().currentDocument;
    if (!doc?.assessment) return;
    const { [blockId]: _, ...responseAreas } = doc.assessment.responseAreas;
    const responseAreaOrder = doc.assessment.responseAreaOrder.filter((id) => id !== blockId);
    set({
      currentDocument: {
        ...doc,
        assessment: { ...doc.assessment, responseAreas, responseAreaOrder },
      },
    });
  },
}));
